"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { VoiceGuidePlayer, type VoiceGuideTrack } from "./VoiceGuidePlayer";

interface VoiceGuideButtonProps {
  track: VoiceGuideTrack;
  label?: string;
  className?: string;
}

/**
 * A compact trigger that expands inline to show the VoiceGuidePlayer.
 * Designed to be embedded contextually on any screen.
 */
export function VoiceGuideButton({ track, label = "Listen to voice guide", className }: VoiceGuideButtonProps) {
  const [open, setOpen] = useState(false);

  if (open) {
    return (
      <div className={cn("space-y-2", className)}>
        <VoiceGuidePlayer
          track={track}
          onEnded={() => setOpen(false)}
        />
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-muted underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label="Dismiss voice guide"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setOpen(true)}
      aria-label={label}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        className
      )}
    >
      <HeadphonesIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
      {label}
    </button>
  );
}

function HeadphonesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 18v-6a9 9 0 0118 0v6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
    </svg>
  );
}
